import React, { useState } from "react";
import {
  Box,
  Card,
  CardBody,
  CardFooter,
  Heading,
  Text,
  Button,
  Layer,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from "grommet";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config/config";

const AssessmentCard = ({
  assessment,
  getTopicName,
  user_id,
  assessment_id,
  manager_id,
}) => {
  const navigate = useNavigate();
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(null);
  const [error, setError] = useState(null);

  const formatDueDate = (dueBy) => {
    const date = new Date(dueBy);
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true, // Use 12-hour format (AM/PM)
    };
    return date.toLocaleString("en-US", options);
  };

  const topics = [
    getTopicName(assessment.topic1),
    getTopicName(assessment.topic2),
    getTopicName(assessment.topic3),
    getTopicName(assessment.topic4),
    getTopicName(assessment.topic5),
    getTopicName(assessment.topic6),
  ].filter(Boolean);

  const isDisabled = topics.length === 0;

  const handleAcceptAssessment = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_assessment_status`,
        {
          user_id,
          assessment_id,
          invite_status: 2,
          manager_id: manager_id,
        },
      );

      
      setInviteStatus(2);
      setIsLoading(false);
      if (response) {
        navigate(
          `/${assessment.user_id}/quiz-settings-manager/${assessment.assesment_id}`,
        );
      }
    } catch (error) {
      console.error("Error accepting invite:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      setIsLoading(false);
    }
  };

  // Handler for rejecting the assessment (invite_status: 2, sets is_complete to -1)
  const handleRejectAssessment = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/user/update_assessment_status`,
        {
          user_id,
          assessment_id,
          invite_status: -1,
          manager_id: manager_id,
        },
      );
      if (response) {
        window.location.reload();
      }
      
      setInviteStatus(-1);
      setIsLoading(false);
    } catch (error) {
      console.error("Error rejecting invite:", error);
      setError(
        error.response?.data?.message || error.message || "An error occurred.",
      );
      setIsLoading(false);
    }
  };

  return (
    <Card
      background="light-1"
      pad="small"
      margin="xsmall"
      elevation="small"
      round="small"
      style={{
        border: "1px solid #E0E0E0",
        width: "320px",
        height: "320px",
      }}
    >
      <CardBody pad="small" margin={{ bottom: "small" }}>
        <Box fill="vertical" justify="between" gap="small">
          {/* Assigned By */}
          <Text size="small" color="dark-4">
            {assessment.manager_name !== "unknown" && (
              <Text size="small" color="dark-4">
                Assigned by Manager:{" "}
                <strong>{assessment.manager_name || "N/A"}</strong>
              </Text>
            )}
          </Text>

          {/* Creation Date & Time */}
          <Box direction="row" gap="xsmall" align="center">
            <Text size="small" color="dark-4">
              Due by:
            </Text>
            <Text size="small" color="dark-4" weight="bold">
              {formatDueDate(assessment.due_by) || "N/A"}
            </Text>
          </Box>

          {/* Questions Count */}
          <Box direction="row" gap="xsmall" align="center">
            <Text size="small" color="dark-4">
              Number of Questions:
            </Text>
            <Text size="small" color="dark-4" weight="bold">
              {isDisabled ? 0 : assessment.query_count || "N/A"}
            </Text>
          </Box>

          {/* Topics - Show list or button if too many */}
          <Box gap="xsmall">
            {topics.length > 0 ? (
              <Button
                label="View Topics"
                secondary
                size="small"
                onClick={() => setShowTopicsModal(true)}
              />
            ) : (
              topics.map((topic, index) => (
                <Text key={index} size="small" color="dark-3">
                  {topic}
                </Text>
              ))
            )}
            {topics.length === 0 && (
              <Text size="small" color="status-unknown">
                No topics assigned
              </Text>
            )}
          </Box>
        </Box>
      </CardBody>

      {/* Single Large Start Assessment Button */}
      <CardFooter
        pad="small"
        background="light-2"
        round={{ corner: "bottom", size: "small" }}
        style={{ borderTop: "1px solid #E0E0E0" }}
      >
        {assessment.is_complete === 0 && (
          <Box fill="horizontal" gap="xsmall">
            {error && (
              <Text size="small" color="status-critical">
                {error}
              </Text>
            )}
            <Box direction="row" fill="horizontal">
              <Button
                label={isLoading ? "..." : "Accept"}
                onClick={handleAcceptAssessment}
                primary
                size="medium"
                fill="horizontal"
                style={{ minWidth: "48%", marginRight: "4%" }}
                disabled={topics.length === 0 || isLoading}
              />
              <Button
                label={isLoading ? "..." : "Reject"}
                onClick={handleRejectAssessment}
                secondary
                size="medium"
                fill="horizontal"
                style={{ minWidth: "48%" }}
                disabled={isLoading}
              />
            </Box>
          </Box>
        )}

        {assessment.is_complete === 2 && (
          <Button
            label="Start Assessment"
            onClick={() =>
              navigate(
                `/${assessment.user_id}/quiz-settings-manager/${assessment.assesment_id}`,
              )
            }
            primary
            size="medium"
            fill="horizontal"
            disabled={isDisabled} // Disable button if no topics assigned
            title={
              isDisabled ? "No topics assigned. Cannot start assessment." : ""
            }
            style={{
              minWidth: "100%",
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          />
        )}
      </CardFooter>

      {/* Modal for Topics if List is Large */}
      {showTopicsModal && (
        <Layer
          onEsc={() => setShowTopicsModal(false)}
          onClickOutside={() => setShowTopicsModal(false)}
        >
          <Box pad="small" width="large" margin="small">
            <Heading level="3" margin={{ bottom: "small" }}>
              Topics for this Assessment
            </Heading>
            <Table align="center">
              <TableHeader>
                <TableRow>
                  <TableCell scope="col" border="bottom">
                    <Text weight="bold">Topic</Text>
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topics.map((topic, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Text>{topic}</Text>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box align="center" pad="medium">
              <Button
                label="Close"
                onClick={() => setShowTopicsModal(false)}
                primary
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Card>
  );
};

export default AssessmentCard;
